import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { Container } from "reactstrap";
import styled from "styled-components";

import { TopUpSheet } from "../../components/Credits";
import NewFooter from "../../components/Footers/NewFooter";
import NewNavbar from "../../components/Navbar/NewNavbar";
import { API_URL } from "../../constants";
import { formatCredits, planProduct } from "../../constants/credits";
import { useAccount } from "../../contexts/AccountContext";
import useAccountSettingsSave from "../../hooks/useAccountSettingsSave";
import { authHeader, clearToken, getToken, setToken, signOutEverywhere } from "../../utils/auth";
import { fileToImageDataUrl } from "../../utils/image";
import { storageGet, storageSet } from "../../utils/storage";
import {
  creditsDateLine,
  formatShortDate,
  formatSignedCredits,
  isCalledOutKind,
  planLabelFor,
} from "./creditsCopy";

/** @typedef {import("../../contexts/AccountContext").Credits} Credits */

// Where the Stripe legs (/success, /cancel) come back to after a top-up here.
const ACCOUNT_CREDITS_PATH = "/account#credits";
/** @typedef {import("./creditsCopy").LedgerEntry} LedgerEntry */

/**
 * @typedef {object} Branding
 * @property {string} [name]
 * @property {string} [brokerage]
 * @property {string} [phone]
 * @property {string} [accent_color]
 * @property {string} [logo_url]
 * @property {string} [headshot_url]
 * @property {boolean} [show_logo]
 * @property {boolean} [show_headshot]
 * @property {string} [style_prompt]
 */

/**
 * @typedef {object} BrandingDraft
 * @property {string} brokerage
 * @property {string} phone
 * @property {string} accent_color
 * @property {string} logo_url
 * @property {string} headshot_url
 * @property {boolean} show_logo
 * @property {boolean} show_headshot
 * @property {string} style_prompt
 */

/**
 * @typedef {object} Subscription
 * @property {string} status
 * @property {number} reports_used
 * @property {number} reports_per_period
 * @property {number} reports_remaining
 * @property {string | null | undefined} [current_period_end]
 */

/**
 * @typedef {object} CmaParams
 * @property {number | string} [radius_miles]
 * @property {number | string} [recency_months]
 * @property {string} [property_type]
 * @property {string} [adjustment_notes]
 */

/**
 * @typedef {object} CmaDraft
 * @property {number | string} radius_miles
 * @property {number | string} recency_months
 * @property {string} property_type
 * @property {string} adjustment_notes
 */

/**
 * @typedef {object} ReportTemplate
 * @property {string} name
 * @property {string} base_report_type
 * @property {string[]} excluded_sections
 */

/**
 * @typedef {object} Account
 * @property {string} email
 * @property {string} [name]
 * @property {string} [created_at]
 * @property {Subscription | null} [subscription]
 * @property {Branding} [branding]
 * @property {CmaParams} [cma_params]
 * @property {ReportTemplate[]} [report_templates]
 * @property {string} [default_template]
 */

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, #eef5f4 0%, #f9fafb 340px);
`;

const ContentWrapper = styled.div`
  flex: 1;
  padding: 120px 0 70px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 28px;
`;

const TitleBlock = styled.div`
  min-width: 0;
`;

const Eyebrow = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: #0e7e7a;
  margin-bottom: 6px;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const Subtitle = styled.p`
  font-size: 0.95rem;
  color: #6b7280;
  margin: 6px 0 0;

  a {
    color: #0e7e7a;
    font-weight: 600;
    text-decoration: none;
  }
`;

/** Account + Subscription sit side-by-side on desktop, stack on narrow. */
const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  gap: 20px;
  align-items: start;
  margin-bottom: 20px;

  > * {
    margin-bottom: 0;
  }

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const SignOut = styled.button`
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 999px;
  padding: 8px 18px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: #f3f4f6;
  }
`;

const Card = styled.section`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
`;

const CardLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6b7280;
  margin-bottom: 16px;
`;

// --- Account card ---
const AccountRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Avatar = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #0e7e7a;
  color: #ffffff;
  font-size: 1.4rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const AccountInfo = styled.div`
  min-width: 0;
  flex: 1;
`;

const AccountName = styled.div`
  font-size: 1.15rem;
  font-weight: 700;
  color: #1a1a1a;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const AccountMeta = styled.div`
  font-size: 0.9rem;
  color: #6b7280;
  margin-top: 2px;
`;

const InlineLink = styled.button`
  background: none;
  border: none;
  color: #0e7e7a;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
`;

const NameForm = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const EmailForm = styled.div`
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
`;

const NameInput = styled.input`
  font-size: 1rem;
  color: #1a1a1a;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 12px;

  &:focus {
    outline: none;
    border-color: #0e7e7a;
  }
`;

const SmallButton = styled.button`
  font-size: 0.85rem;
  font-weight: 600;
  border-radius: 8px;
  padding: 8px 14px;
  cursor: pointer;
  border: 1px solid ${(p) => (p.$primary ? "#0e7e7a" : "#d1d5db")};
  background: ${(p) => (p.$primary ? "#0e7e7a" : "#ffffff")};
  color: ${(p) => (p.$primary ? "#ffffff" : "#374151")};

  &:hover {
    opacity: 0.92;
  }
`;

const NoSub = styled.div`
  font-size: 0.95rem;
  color: #4a4a4a;

  a {
    color: #0e7e7a;
    font-weight: 600;
    text-decoration: none;
  }
`;

// --- Plan & credits card (pricing v2) ---
const BalanceHeadline = styled.div`
  font-size: 1.9rem;
  font-weight: 700;
  color: #1a1a1a;
  letter-spacing: -0.02em;
  line-height: 1.1;
`;

const PlanLine = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: #374151;
  margin-top: 6px;
`;

const CreditsMeta = styled.p`
  font-size: 0.88rem;
  color: #6b7280;
  margin: 10px 0 0;
  line-height: 1.5;

  a {
    color: #0e7e7a;
    font-weight: 600;
    text-decoration: none;
  }
`;

// --- Credit activity card ---
const ActivityList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

// A block, not a flex row: the separators are real text so the row reads the
// same to a screen reader or a copy-paste as it does on screen.
const ActivityRow = styled.li`
  font-size: 0.92rem;
  line-height: 1.6;
  color: #374151;
  padding: 9px 0;
  border-top: 1px solid #f0f0f0;

  &:first-child {
    border-top: none;
    padding-top: 0;
  }
`;

const ActivityAmount = styled.span`
  display: inline-block;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  color: #1a1a1a;
  min-width: 4.5ch;
`;

const ActivityKind = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #4b5563;
`;

// --- Profile-completion banner ---
const Banner = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 14px;
  padding: 14px 18px;
  margin-bottom: 20px;
`;

const BannerText = styled.button`
  flex: 1;
  text-align: left;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 0.92rem;
  color: #065f46;

  strong {
    font-weight: 700;
  }
`;

const BannerDismiss = styled.button`
  flex-shrink: 0;
  background: none;
  border: none;
  font-size: 1.3rem;
  line-height: 1;
  color: #059669;
  cursor: pointer;
  padding: 0 4px;

  &:hover {
    color: #065f46;
  }
`;

const Centered = styled.div`
  text-align: center;
  padding: 80px 0;
  color: #4a4a4a;

  h2 {
    font-size: 1.4rem;
    color: #1a1a1a;
    margin-bottom: 8px;
  }
`;

const Spinner = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid #e5e7eb;
  border-top-color: #6b7280;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 16px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

/** @param {string} iso */
const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
};

/**
 * @param {unknown} error
 * @param {string} fallback
 */
const imageErrorMessage = (error, fallback) =>
  error instanceof Error && error.message ? error.message : fallback;

/** The HTTP status of a failed axios call, if it got that far.
 * @param {unknown} error
 */
const httpStatus = (error) =>
  /** @type {{ response?: { status?: number } } | null} */ (error)?.response?.status;

/**
 * @param {unknown} error
 * @param {string} fallback
 */
const apiErrorMessage = (error, fallback) => {
  if (!axios.isAxiosError(error)) return fallback;
  const data = /** @type {unknown} */ (error.response?.data);
  if (data && typeof data === "object" && "error" in data && typeof data.error === "string") {
    return data.error;
  }
  return fallback;
};

const ManageBillingRow = styled.div`
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const ErrorText = styled.span`
  font-size: 0.85rem;
  color: #b91c1c;
`;

// --- Prep branding card ---
const BrandingGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 540px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
`;

const FieldInput = styled.input`
  font-size: 1rem;
  color: #1a1a1a;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 9px 12px;

  &:focus {
    outline: none;
    border-color: #0e7e7a;
  }
`;

const ColorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ImageField = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const ImagePreview = styled.img`
  width: ${(p) => (p.$round ? "56px" : "auto")};
  height: 56px;
  max-width: 140px;
  object-fit: ${(p) => (p.$round ? "cover" : "contain")};
  border-radius: ${(p) => (p.$round ? "50%" : "8px")};
  border: 1px solid #e5e7eb;
  background: #f9fafb;
`;

const UploadLabel = styled.label`
  display: inline-flex;
  align-items: center;
  min-height: 40px;
  padding: 8px 14px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background: #f3f4f6;
  }

  &:focus-within {
    outline: 2px solid #0e7e7a;
    outline-offset: 2px;
  }

  input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    overflow: hidden;
  }
`;

const BrandingActions = styled.div`
  margin-top: 18px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const NoticeText = styled.span`
  font-size: 0.85rem;
  color: #166534;
`;

const ToggleLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;

  input {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
`;

/**
 * @param {{ branding: Branding, onSaved: (branding: Branding) => void }} props
 */
const BrandingCard = ({ branding, onSaved }) => {
  const [draft, setDraft] = useState({
    brokerage: branding.brokerage || "",
    phone: branding.phone || "",
    accent_color: branding.accent_color || "",
    logo_url: branding.logo_url || "",
    headshot_url: branding.headshot_url || "",
    show_logo: branding.show_logo !== false,
    show_headshot: branding.show_headshot !== false,
    style_prompt: branding.style_prompt || "",
  });
  const { busy, error, notice, save, clearNotice, setError } = useAccountSettingsSave({
    buildPayload: () => ({
      brokerage: draft.brokerage.trim(),
      phone: draft.phone.trim(),
      accent_color: draft.accent_color,
      logo_url: draft.logo_url,
      headshot_url: draft.headshot_url,
      show_logo: draft.show_logo,
      show_headshot: draft.show_headshot,
      style_prompt: draft.style_prompt.trim(),
    }),
    onSaved: (data) => onSaved(data.branding),
    successNote: "Branding saved. Every new listing prep will use it.",
    errorFallback: "Couldn't save your branding. Try again.",
  });

  /**
   * @template {keyof BrandingDraft} K
   * @param {K} field
   * @param {BrandingDraft[K]} value
   */
  const set = (field, value) => {
    setDraft((draftValue) => ({ ...draftValue, [field]: value }));
    clearNotice();
  };

  /**
   * @param {"logo_url" | "headshot_url"} field
   * @param {File | undefined} file
   */
  const onImage = async (field, file) => {
    if (!file) return;
    setError("");
    try {
      const dataUrl = await fileToImageDataUrl(file);
      set(field, dataUrl);
    } catch (error) {
      setError(imageErrorMessage(error, "Couldn't use that image."));
    }
  };

  return (
    <Card>
      <CardLabel>Prep branding</CardLabel>
      <NoSub style={{ marginBottom: 16 }}>
        Set your broker info and photos once — every new listing prep uses them, and you can still
        tweak any listing prep in Personalize.
      </NoSub>
      <BrandingGrid>
        <Field>
          Brokerage
          <FieldInput
            value={draft.brokerage}
            placeholder="Your brokerage"
            onChange={(e) => set("brokerage", e.target.value)}
          />
        </Field>
        <Field>
          Phone
          <FieldInput
            value={draft.phone}
            placeholder="(555) 555-5555"
            onChange={(e) => set("phone", e.target.value)}
          />
        </Field>
        <Field>
          Accent color
          <ColorRow>
            <input
              type="color"
              aria-label="Accent color"
              value={draft.accent_color || "#0e7e7a"}
              onChange={(e) => set("accent_color", e.target.value)}
            />
            <span style={{ fontWeight: 500, color: "#6b7280" }}>
              {draft.accent_color || "Default"}
            </span>
            {draft.accent_color && (
              <InlineLink type="button" onClick={() => set("accent_color", "")}>
                Reset
              </InlineLink>
            )}
          </ColorRow>
        </Field>
        <div />
        <Field as="div">
          Brokerage logo
          <ImageField>
            {draft.logo_url && <ImagePreview src={draft.logo_url} alt="Logo preview" />}
            <UploadLabel>
              {draft.logo_url ? "Replace" : "Upload"}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => onImage("logo_url", e.target.files?.[0])}
              />
            </UploadLabel>
            {draft.logo_url && (
              <InlineLink type="button" onClick={() => set("logo_url", "")}>
                Remove
              </InlineLink>
            )}
          </ImageField>
          <ToggleLabel>
            <input
              type="checkbox"
              checked={draft.show_logo}
              onChange={(e) => set("show_logo", e.target.checked)}
            />
            Show logo on your listing preps
          </ToggleLabel>
        </Field>
        <Field as="div">
          Broker photo
          <ImageField>
            {draft.headshot_url && (
              <ImagePreview $round src={draft.headshot_url} alt="Headshot preview" />
            )}
            <UploadLabel>
              {draft.headshot_url ? "Replace" : "Upload"}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => onImage("headshot_url", e.target.files?.[0])}
              />
            </UploadLabel>
            {draft.headshot_url && (
              <InlineLink type="button" onClick={() => set("headshot_url", "")}>
                Remove
              </InlineLink>
            )}
          </ImageField>
          <ToggleLabel>
            <input
              type="checkbox"
              checked={draft.show_headshot}
              onChange={(e) => set("show_headshot", e.target.checked)}
            />
            Show photo on your listing preps
          </ToggleLabel>
        </Field>
      </BrandingGrid>
      <Field as="div" style={{ marginTop: 18 }}>
        Default writing style
        <NoSub style={{ margin: "4px 0 8px", fontWeight: 400 }}>
          Applied to every listing you prepare — set the tone, voice, and phrasing you like. It
          steers the writing but never the numbers or Fair Housing rules, and you can still
          fine-tune any prep in Personalize.
        </NoSub>
        <FieldInput
          as="textarea"
          rows={4}
          maxLength={2000}
          style={{ resize: "vertical", minHeight: 92, fontFamily: "inherit", lineHeight: 1.5 }}
          value={draft.style_prompt}
          placeholder={
            'e.g. "Warm and concise. Lead with lifestyle, avoid hype. Short paragraphs, active voice."'
          }
          onChange={(e) => set("style_prompt", e.target.value)}
        />
      </Field>
      <BrandingActions>
        <SmallButton $primary type="button" onClick={save} disabled={busy}>
          {busy ? "Saving…" : "Save branding"}
        </SmallButton>
        {error && <ErrorText>{error}</ErrorText>}
        {notice && <NoticeText>{notice}</NoticeText>}
      </BrandingActions>
    </Card>
  );
};

/**
 * @param {{
 *   subscription: Subscription | null | undefined,
 *   onManageBilling: () => void,
 *   billingBusy: boolean,
 *   billingError: string,
 * }} props
 */
/**
 * The Plan & credits card. Everything on it is a fact about the
 * plan — the balance (or "—" when unknown, never 0), the plan and interval,
 * the honest reset/pool date, the Free plan's watermark note (the second of
 * the product's two mentions), and two plain actions.
 *
 * @param {{
 *   credits: Credits | null,
 *   onBuyCredits: () => void,
 *   onManageBilling: () => void,
 *   billingBusy: boolean,
 *   billingError: string,
 * }} props
 */
const PlanCreditsCard = ({ credits, onBuyCredits, onManageBilling, billingBusy, billingError }) => {
  const product = credits ? planProduct(credits.plan_key) : null;
  const free = product === "free";
  return (
    <Card id="credits">
      <CardLabel>Plan &amp; credits</CardLabel>
      <BalanceHeadline data-testid="credits-balance">
        {credits ? formatCredits(credits.balance) : "— credits"}
      </BalanceHeadline>
      {credits ? (
        <>
          <PlanLine>{planLabelFor(credits.plan_key)}</PlanLine>
          <CreditsMeta>{creditsDateLine(credits)}</CreditsMeta>
        </>
      ) : (
        <CreditsMeta>Your balance isn't available right now.</CreditsMeta>
      )}
      {free && (
        <CreditsMeta>
          Exports on the Free plan carry a small Listino mark. Upgrading removes it from everything
          you've prepared. <Link to="/pricing">See plans</Link>
        </CreditsMeta>
      )}
      <ManageBillingRow>
        <SmallButton type="button" onClick={onBuyCredits}>
          Buy credits
        </SmallButton>
        {free || !credits ? (
          <SmallButton as={Link} to="/pricing">
            Change plan
          </SmallButton>
        ) : (
          <SmallButton type="button" onClick={onManageBilling} disabled={billingBusy}>
            {billingBusy ? "Opening…" : "Manage billing"}
          </SmallButton>
        )}
        {billingError && <ErrorText>{billingError}</ErrorText>}
      </ManageBillingRow>
    </Card>
  );
};

/**
 * Pricing v2: the ledger, as a plain list — "−500 · Listing analysis · 123
 * Main St · Aug 14". Refunds and expiries are called out on the row. No
 * charts, no totals.
 *
 * @param {{
 *   state: "loading" | "ok" | "error" | "unavailable",
 *   entries: LedgerEntry[],
 * }} props
 */
const CreditActivityCard = ({ state, entries }) => {
  let content;
  if (state === "loading") {
    content = <NoSub>Loading…</NoSub>;
  } else if (state === "unavailable") {
    content = <NoSub>Credit activity isn't available yet.</NoSub>;
  } else if (state === "error") {
    content = <NoSub>We couldn't load your credit activity right now.</NoSub>;
  } else if (entries.length === 0) {
    content = <NoSub>Nothing yet.</NoSub>;
  } else {
    content = (
      <ActivityList>
        {entries.map((entry, index) => (
          <ActivityRow key={`${entry.created_at}-${index}`} data-kind={entry.kind}>
            <ActivityAmount>{formatSignedCredits(entry.amount)}</ActivityAmount>
            {" · "}
            {isCalledOutKind(entry) ? (
              <ActivityKind data-callout="true">{entry.label}</ActivityKind>
            ) : (
              <span>{entry.label}</span>
            )}
            {entry.listing_address ? ` · ${entry.listing_address}` : ""}
            {` · ${formatShortDate(entry.created_at)}`}
          </ActivityRow>
        ))}
      </ActivityList>
    );
  }
  return (
    <Card>
      <CardLabel>Credit activity</CardLabel>
      {content}
    </Card>
  );
};

/**
 * Set or change the sign-in password (pricing-v2 W3, D8: passwords are the
 * default sign-in method; emailed codes stay the backup and the recovery
 * path — there is no separate forgot-password flow).
 *
 * The current password is deliberately NOT required: possession of the
 * signed-in session is the bar, matching the email-change flow above, which
 * never re-proves the old credential either (it verifies control of the NEW
 * address only — a session holder could take the account through it anyway).
 * The API compensates by revoking every other session on save and returning
 * a fresh token for this one, which must be stored immediately.
 *
 * @param {{ passwordSet: boolean | null, onSaved: () => void }} props
 */
const PasswordCard = ({ passwordSet, onSaved }) => {
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const save = async () => {
    if (draft.length < 10) {
      setError("Use at least 10 characters.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const resp = await axios.post(
        `${API_URL}/auth/set-password/`,
        { password: draft },
        { headers: authHeader() }
      );
      // Saving revokes every outstanding session (including this one) and
      // returns its replacement — store it before anything else runs.
      if (resp.data?.token) setToken(resp.data.token);
      setDraft("");
      setNotice("Password saved. Any other signed-in devices were signed out.");
      onSaved();
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't save that password. Try again."));
    } finally {
      setBusy(false);
    }
  };

  let copy;
  if (passwordSet === true) {
    copy =
      "Signing in with your password is the default. Enter a new one to change it. Forgot it? Sign in with an emailed code, then set a new one here.";
  } else if (passwordSet === false) {
    copy =
      "Your account signs in with emailed codes. Add a password to make signing in faster — the emailed code keeps working as a backup.";
  } else {
    copy =
      "Add or change the password you sign in with. Forgot it? Sign in with an emailed code, then set a new one here.";
  }
  let buttonLabel;
  if (passwordSet === true) {
    buttonLabel = "Change password";
  } else if (passwordSet === false) {
    buttonLabel = "Set password";
  } else {
    buttonLabel = "Save password";
  }

  return (
    <Card>
      <CardLabel>Password</CardLabel>
      <NoSub style={{ marginBottom: 16 }}>{copy}</NoSub>
      <NameForm>
        <NameInput
          type="password"
          aria-label="New password"
          placeholder="At least 10 characters"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setNotice("");
            setError("");
          }}
        />
        <SmallButton $primary type="button" onClick={save} disabled={busy || !draft}>
          {busy ? "Saving…" : buttonLabel}
        </SmallButton>
      </NameForm>
      {(error || notice) && (
        <BrandingActions>
          {error && <ErrorText>{error}</ErrorText>}
          {notice && <NoticeText>{notice}</NoticeText>}
        </BrandingActions>
      )}
    </Card>
  );
};

const CMA_PROPERTY_TYPES = [
  { key: "", label: "Any" },
  { key: "single_family", label: "Single-family" },
  { key: "condo", label: "Condo" },
  { key: "townhouse", label: "Townhouse" },
  { key: "multi_family", label: "Multi-family" },
  { key: "land", label: "Land / lot" },
];

/** Account-level default Quick CMA preferences (how the agent likes comps run). */
/**
 * @param {{ cmaParams: CmaParams, onSaved: (params: CmaParams) => void }} props
 */
const CmaCard = ({ cmaParams, onSaved }) => {
  const [draft, setDraft] = useState(
    /** @type {CmaDraft} */ ({
      radius_miles: cmaParams.radius_miles ?? "",
      recency_months: cmaParams.recency_months ?? "",
      property_type: cmaParams.property_type ?? "",
      adjustment_notes: cmaParams.adjustment_notes ?? "",
    })
  );
  const { busy, error, notice, save, clearNotice } = useAccountSettingsSave({
    buildPayload: () => {
      const params = /** @type {CmaParams} */ ({});
      if (draft.radius_miles !== "") params.radius_miles = Number(draft.radius_miles);
      if (draft.recency_months !== "") params.recency_months = Number(draft.recency_months);
      if (draft.property_type) params.property_type = draft.property_type;
      if (draft.adjustment_notes.trim()) params.adjustment_notes = draft.adjustment_notes.trim();
      return { cma_params: params };
    },
    onSaved: (data) => onSaved(data.cma_params || {}),
    successNote: "Saved. New preps will run comps this way.",
    errorFallback: "Couldn't save. Try again.",
  });
  /**
   * @template {keyof CmaDraft} K
   * @param {K} key
   * @param {CmaDraft[K]} value
   */
  const set = (key, value) => {
    setDraft((draftValue) => ({ ...draftValue, [key]: value }));
    clearNotice();
  };
  return (
    <Card>
      <CardLabel>Quick CMA defaults</CardLabel>
      <NoSub style={{ marginBottom: 16 }}>
        Tell us how you like comps run — every new prep starts from these. The numbers always stay
        grounded in the data.
      </NoSub>
      <BrandingGrid>
        <Field>
          Comp radius (miles)
          <FieldInput
            type="number"
            min="1"
            max="100"
            value={draft.radius_miles}
            placeholder="e.g. 3"
            onChange={(e) => set("radius_miles", e.target.value)}
          />
        </Field>
        <Field>
          Recency (months)
          <FieldInput
            type="number"
            min="1"
            max="120"
            value={draft.recency_months}
            placeholder="e.g. 6"
            onChange={(e) => set("recency_months", e.target.value)}
          />
        </Field>
        <Field>
          Property type
          <FieldInput
            as="select"
            value={draft.property_type}
            onChange={(e) => set("property_type", e.target.value)}
          >
            {CMA_PROPERTY_TYPES.map((t) => (
              <option key={t.key} value={t.key}>
                {t.label}
              </option>
            ))}
          </FieldInput>
        </Field>
      </BrandingGrid>
      <Field as="div" style={{ marginTop: 14 }}>
        Adjustment guidance
        <NoSub style={{ margin: "4px 0 8px", fontWeight: 400 }}>
          How you like to weigh differences — e.g. "price updated kitchens higher, discount smaller
          lots".
        </NoSub>
        <FieldInput
          as="textarea"
          rows={3}
          maxLength={2000}
          style={{ resize: "vertical", minHeight: 72, fontFamily: "inherit", lineHeight: 1.5 }}
          value={draft.adjustment_notes}
          onChange={(e) => set("adjustment_notes", e.target.value)}
        />
      </Field>
      <BrandingActions>
        <SmallButton $primary type="button" onClick={save} disabled={busy}>
          {busy ? "Saving…" : "Save CMA defaults"}
        </SmallButton>
        {error && <ErrorText>{error}</ErrorText>}
        {notice && <NoticeText>{notice}</NoticeText>}
      </BrandingActions>
    </Card>
  );
};

const TOGGLEABLE_SECTIONS = [
  { key: "comparable_analysis", label: "Comparables & pricing" },
  { key: "neighborhood_overview", label: "Neighborhood" },
  { key: "listing_rewrite", label: "Listing description" },
  { key: "photo_sequence", label: "Photo order" },
  { key: "photo_analysis", label: "Photo recommendations" },
  { key: "tax_records", label: "Tax history" },
  { key: "price_history", label: "Price history" },
];
const BASE_REPORT_TYPES = [
  { key: "unified_strategy", label: "Standard listing strategy" },
  { key: "cma_first_strategy", label: "Pricing & comps first" },
  { key: "pre_listing_strategy", label: "Pre-listing prep" },
];

const TemplateTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;

const TemplateTab = styled.button`
  font-size: 0.85rem;
  font-weight: 600;
  border-radius: 999px;
  padding: 8px 16px;
  min-height: 40px;
  cursor: pointer;
  border: 1px solid ${(p) => (p.$active ? "#0e7e7a" : "#d1d5db")};
  background: ${(p) => (p.$active ? "#e6f4f3" : "#ffffff")};
  color: ${(p) => (p.$active ? "#0b605d" : "#374151")};
`;

/**
 * @param {ReportTemplate[] | null | undefined} list
 * @returns {ReportTemplate[]}
 */
const normalizeTemplates = (list) =>
  (list || []).map((template) => ({
    name: template.name || "Default",
    base_report_type: template.base_report_type || "unified_strategy",
    excluded_sections: [...(template.excluded_sections || [])],
  }));

/** Named section templates — e.g. "Relist" vs "New listing" vs "Rental" — each
 * a prep style + the sections it includes. The default governs new preps; a
 * specific template can be picked per prep at checkout. Executive summary +
 * conclusion always stay, so they aren't shown. */
/**
 * @param {{
 *   templates: ReportTemplate[],
 *   defaultTemplate: string,
 *   onSaved: (templates: ReportTemplate[], defaultTemplate: string) => void,
 * }} props
 */
const SectionsCard = ({ templates, defaultTemplate, onSaved }) => {
  const initial = normalizeTemplates(
    (templates || []).length
      ? templates
      : [{ name: "Default", base_report_type: "unified_strategy", excluded_sections: [] }]
  );
  const [drafts, setDrafts] = useState(initial);
  const [defaultName, setDefaultName] = useState(
    defaultTemplate && initial.some((t) => t.name === defaultTemplate)
      ? defaultTemplate
      : initial[0].name
  );
  const [selected, setSelected] = useState(0);
  const { busy, error, notice, save, clearNotice, setError } = useAccountSettingsSave({
    buildPayload: () => ({
      report_templates: drafts.map((t) => ({ ...t, name: t.name.trim() })),
      default_template: defaultName,
    }),
    onSaved: (data) => onSaved(data.report_templates || [], data.default_template || ""),
    successNote: "Saved. New preps will use these templates.",
    errorFallback: "Couldn't save. Try again.",
  });

  const current = drafts[selected] || drafts[0];
  /** @param {Partial<ReportTemplate>} patch */
  const setCurrent = (patch) => {
    clearNotice();
    setDrafts((draftValues) =>
      draftValues.map((template, index) =>
        index === selected ? { ...template, ...patch } : template
      )
    );
  };

  /** @param {string} name */
  const renameCurrent = (name) => {
    // Keep the default pointer following a rename of the default template.
    if (current.name === defaultName) setDefaultName(name);
    setCurrent({ name });
  };
  /** @param {string} key */
  const toggleSection = (key) => {
    const excluded = new Set(current.excluded_sections);
    if (excluded.has(key)) excluded.delete(key);
    else excluded.add(key);
    setCurrent({ excluded_sections: [...excluded] });
  };
  const addTemplate = () => {
    clearNotice();
    const existingNames = new Set(drafts.map((template) => template.name));
    let n = drafts.length + 1;
    while (existingNames.has(`Template ${n}`)) n += 1;
    const templateName = `Template ${n}`;
    setDrafts((d) => [
      ...d,
      { name: templateName, base_report_type: "unified_strategy", excluded_sections: [] },
    ]);
    setSelected(drafts.length);
  };
  const removeCurrent = () => {
    if (drafts.length <= 1) return;
    clearNotice();
    const removed = current.name;
    const next = drafts.filter((_, i) => i !== selected);
    setDrafts(next);
    setSelected(0);
    if (removed === defaultName) setDefaultName(next[0].name);
  };
  const guardedSave = () => {
    const names = drafts.map((t) => t.name.trim());
    if (names.some((n) => !n)) {
      setError("Every template needs a name.");
      return;
    }
    if (new Set(names).size !== names.length) {
      setError("Template names must be unique.");
      return;
    }
    save();
  };

  return (
    <Card>
      <CardLabel>Prep templates</CardLabel>
      <NoSub style={{ marginBottom: 16 }}>
        Save a template per situation — a relist, a new listing, a rental — each with its own prep
        style and sections. The default governs new preps, and you can pick a different template
        when you prepare a listing. You can still drop sections from an individual prep in
        Personalize.
      </NoSub>
      <TemplateTabs>
        {drafts.map((t, i) => (
          <TemplateTab
            key={`${i}-${t.name}`}
            type="button"
            $active={i === selected}
            onClick={() => setSelected(i)}
          >
            {t.name || "Unnamed"}
            {t.name === defaultName ? " ★" : ""}
          </TemplateTab>
        ))}
        <TemplateTab type="button" onClick={addTemplate}>
          + New template
        </TemplateTab>
      </TemplateTabs>
      <BrandingGrid>
        <Field>
          Template name
          <FieldInput
            value={current.name}
            placeholder="e.g. Rental"
            onChange={(e) => renameCurrent(e.target.value)}
          />
        </Field>
        <Field>
          Prep style
          <FieldInput
            as="select"
            value={current.base_report_type}
            onChange={(e) => setCurrent({ base_report_type: e.target.value })}
          >
            {BASE_REPORT_TYPES.map((t) => (
              <option key={t.key} value={t.key}>
                {t.label}
              </option>
            ))}
          </FieldInput>
        </Field>
      </BrandingGrid>
      <div style={{ marginTop: 16, display: "flex", flexWrap: "wrap", gap: "10px 20px" }}>
        {TOGGLEABLE_SECTIONS.map((s) => (
          <ToggleLabel key={s.key}>
            <input
              type="checkbox"
              checked={!current.excluded_sections.includes(s.key)}
              onChange={() => toggleSection(s.key)}
            />
            {s.label}
          </ToggleLabel>
        ))}
      </div>
      <div style={{ marginTop: 14, display: "flex", gap: 16, alignItems: "center" }}>
        <ToggleLabel style={{ marginTop: 0 }}>
          <input
            type="checkbox"
            checked={current.name === defaultName}
            disabled={current.name === defaultName}
            onChange={() => setDefaultName(current.name)}
          />
          Default for new preps
        </ToggleLabel>
        {drafts.length > 1 && (
          <InlineLink type="button" onClick={removeCurrent}>
            Delete this template
          </InlineLink>
        )}
      </div>
      <BrandingActions>
        <SmallButton $primary type="button" onClick={guardedSave} disabled={busy}>
          {busy ? "Saving…" : "Save templates"}
        </SmallButton>
        {error && <ErrorText>{error}</ErrorText>}
        {notice && <NoticeText>{notice}</NoticeText>}
      </BrandingActions>
    </Card>
  );
};

const AccountPage = () => {
  const history = useHistory();
  // /auth/me/ comes from the shared account context (one fetch for the app;
  // the balance on it is refreshed after every spend). The page keeps a local
  // copy because its saves (name, email, branding, templates) update it in
  // place from each PATCH response.
  const { account: sharedAccount, credits, error: accountError } = useAccount();
  const [account, setAccount] = useState(/** @type {Account | null} */ (null));
  const [editingName, setEditingName] = useState(false);
  const [buyingCredits, setBuyingCredits] = useState(false);
  const [activityState, setActivityState] = useState(
    /** @type {"loading" | "ok" | "error" | "unavailable"} */ ("loading")
  );
  const [activity, setActivity] = useState(/** @type {LedgerEntry[]} */ ([]));
  const [nameDraft, setNameDraft] = useState("");
  const [billingBusy, setBillingBusy] = useState(false);
  const [billingError, setBillingError] = useState("");
  const [emailStep, setEmailStep] = useState(/** @type {"idle" | "email" | "code"} */ ("idle"));
  const [emailDraft, setEmailDraft] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [emailNotice, setEmailNotice] = useState("");
  const [bannerDismissed, setBannerDismissed] = useState(
    () => storageGet("liq_profile_banner_dismissed", "local") === "1"
  );
  // null = unknown (still loading, or the status call failed) — the card then
  // renders neutral set-or-change copy instead of guessing.
  const [passwordSet, setPasswordSet] = useState(/** @type {boolean | null} */ (null));
  const brandingRef = useRef(/** @type {HTMLDivElement | null} */ (null));

  const signOut = useCallback(() => {
    signOutEverywhere();
    history.push("/login");
  }, [history]);

  const scrollToBranding = useCallback(() => {
    brandingRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  const dismissBanner = useCallback(() => {
    storageSet("liq_profile_banner_dismissed", "1", "local");
    setBannerDismissed(true);
  }, []);

  const manageBilling = useCallback(async () => {
    setBillingBusy(true);
    setBillingError("");
    try {
      const resp = await axios.post(
        `${API_URL}/auth/me/billing-portal/`,
        {},
        { headers: authHeader() }
      );
      if (resp.data?.url) {
        window.location.href = resp.data.url;
      } else {
        setBillingError("Billing isn't available right now.");
        setBillingBusy(false);
      }
    } catch (error) {
      setBillingError(apiErrorMessage(error, "Couldn't open billing. Try again."));
      setBillingBusy(false);
    }
  }, []);

  const sendEmailCode = useCallback(async () => {
    if (!emailDraft.trim()) return;
    setEmailBusy(true);
    setEmailError("");
    try {
      await axios.post(
        `${API_URL}/auth/change-email/request/`,
        { email: emailDraft.trim() },
        { headers: authHeader() }
      );
      setEmailStep("code");
    } catch (error) {
      setEmailError(apiErrorMessage(error, "Couldn't send a code. Try again."));
    } finally {
      setEmailBusy(false);
    }
  }, [emailDraft]);

  const confirmEmailChange = useCallback(async () => {
    if (!emailCode.trim()) return;
    setEmailBusy(true);
    setEmailError("");
    try {
      const resp = await axios.post(
        `${API_URL}/auth/change-email/confirm/`,
        { email: emailDraft.trim(), code: emailCode.trim() },
        { headers: authHeader() }
      );
      if (resp.data?.token) setToken(resp.data.token);
      setAccount((currentAccount) =>
        currentAccount ? { ...currentAccount, email: resp.data.email } : currentAccount
      );
      setEmailStep("idle");
      setEmailNotice("Email updated.");
    } catch (error) {
      setEmailError(apiErrorMessage(error, "That code didn't work. Try again."));
    } finally {
      setEmailBusy(false);
    }
  }, [emailDraft, emailCode]);

  useEffect(() => {
    if (!getToken()) history.push("/login");
  }, [history]);

  useEffect(() => {
    if (sharedAccount) setAccount(sharedAccount);
  }, [sharedAccount]);

  // Separate from /auth/me/ on purpose: the account payload contract is owned
  // by the credits workstream right now, so password state rides its own tiny
  // endpoint. Non-fatal — on failure the card just shows neutral copy. It is
  // also this page's own authenticated call, so a revoked session (another
  // device set a password) is caught here: the shared context only reports
  // "failed", not why.
  useEffect(() => {
    if (!getToken()) return;
    axios
      .get(`${API_URL}/auth/password-status/`, { headers: authHeader() })
      .then((resp) => setPasswordSet(resp.data?.password_set === true))
      .catch((error) => {
        if (httpStatus(error) === 401) {
          clearToken();
          history.push("/login");
        }
      });
  }, [history]);

  // Credits: the ledger, fetched once. 503 means the ledger is unavailable.
  useEffect(() => {
    if (!getToken()) return;
    axios
      .get(`${API_URL}/auth/me/credits/`, { headers: authHeader() })
      .then((resp) => {
        setActivity(Array.isArray(resp.data?.entries) ? resp.data.entries : []);
        setActivityState("ok");
      })
      .catch((error) => {
        setActivityState(httpStatus(error) === 503 ? "unavailable" : "error");
      });
  }, []);

  // Ready once the shared account has arrived; a failed fetch is the error
  // state; anything else (including the signed-out redirect above) is loading.
  const status = account ? "ok" : accountError ? "error" : "loading";

  const startEditName = () => {
    setNameDraft(account?.name || "");
    setEditingName(true);
  };

  const saveName = async () => {
    try {
      const resp = await axios.patch(
        `${API_URL}/auth/me/`,
        { name: nameDraft.trim() },
        { headers: authHeader() }
      );
      setAccount(resp.data);
      setEditingName(false);
    } catch (err) {
      console.error("Name update failed:", err);
    }
  };

  const startChangeEmail = () => {
    setEmailDraft("");
    setEmailCode("");
    setEmailError("");
    setEmailNotice("");
    setEmailStep("email");
  };

  const cancelChangeEmail = () => {
    setEmailStep("idle");
    setEmailError("");
  };

  let body;
  if (status === "loading") {
    body = (
      <Centered>
        <Spinner />
        <p>Loading your account…</p>
      </Centered>
    );
  } else if (status === "error" || !account) {
    body = (
      <Centered>
        <h2>Something went wrong</h2>
        <p>
          We couldn't load your account. Try refreshing, or email{" "}
          <a href="mailto:help@listino.ai">help@listino.ai</a>.
        </p>
      </Centered>
    );
  } else {
    const initial = (account.name || account.email || "?").trim().charAt(0).toUpperCase();
    const branding = account.branding || {};
    const profileIncomplete = !branding.logo_url || !branding.headshot_url;
    body = (
      <>
        {profileIncomplete && !bannerDismissed && (
          <Banner>
            <BannerText type="button" onClick={scrollToBranding}>
              <strong>Finish your broker profile.</strong> Add your{" "}
              {!branding.logo_url && !branding.headshot_url
                ? "brokerage logo and photo"
                : !branding.logo_url
                  ? "brokerage logo"
                  : "broker photo"}{" "}
              so every listing prep is fully branded.
            </BannerText>
            <BannerDismiss type="button" aria-label="Dismiss" onClick={dismissBanner}>
              ×
            </BannerDismiss>
          </Banner>
        )}
        <SummaryGrid>
          <Card>
            <CardLabel>Account</CardLabel>
            <AccountRow>
              <Avatar>{initial}</Avatar>
              <AccountInfo>
                {editingName ? (
                  <NameForm>
                    <NameInput
                      value={nameDraft}
                      placeholder="Your name"
                      onChange={(e) => setNameDraft(e.target.value)}
                    />
                    <SmallButton $primary type="button" onClick={saveName}>
                      Save
                    </SmallButton>
                    <SmallButton type="button" onClick={() => setEditingName(false)}>
                      Cancel
                    </SmallButton>
                  </NameForm>
                ) : (
                  <AccountName>
                    {account.name || "Add your name"}
                    <InlineLink type="button" onClick={startEditName}>
                      Edit name
                    </InlineLink>
                  </AccountName>
                )}
                <AccountMeta>
                  <span>{account.email}</span>
                  {emailStep === "idle" && (
                    <InlineLink type="button" onClick={startChangeEmail} style={{ marginLeft: 10 }}>
                      Change email
                    </InlineLink>
                  )}
                  {emailNotice && (
                    <span style={{ marginLeft: 10, color: "#166534" }}>{emailNotice}</span>
                  )}
                </AccountMeta>
                {emailStep !== "idle" && (
                  <EmailForm>
                    {emailStep === "email" ? (
                      <NameForm>
                        <NameInput
                          type="email"
                          aria-label="New email address"
                          placeholder="new@email.com"
                          value={emailDraft}
                          onChange={(e) => setEmailDraft(e.target.value)}
                        />
                        <SmallButton
                          $primary
                          type="button"
                          onClick={sendEmailCode}
                          disabled={emailBusy || !emailDraft.trim()}
                        >
                          {emailBusy ? "Sending…" : "Send code"}
                        </SmallButton>
                        <SmallButton type="button" onClick={cancelChangeEmail}>
                          Cancel
                        </SmallButton>
                      </NameForm>
                    ) : (
                      <>
                        <AccountMeta>Enter the code we sent to {emailDraft}.</AccountMeta>
                        <NameForm>
                          <NameInput
                            inputMode="numeric"
                            maxLength={6}
                            aria-label="Email change code"
                            placeholder="000000"
                            value={emailCode}
                            onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, ""))}
                          />
                          <SmallButton
                            $primary
                            type="button"
                            onClick={confirmEmailChange}
                            disabled={emailBusy || !emailCode.trim()}
                          >
                            {emailBusy ? "Confirming…" : "Confirm"}
                          </SmallButton>
                          <SmallButton type="button" onClick={cancelChangeEmail}>
                            Cancel
                          </SmallButton>
                        </NameForm>
                      </>
                    )}
                    {emailError && <ErrorText>{emailError}</ErrorText>}
                  </EmailForm>
                )}
                {account.created_at && (
                  <AccountMeta>Member since {formatDate(account.created_at)}</AccountMeta>
                )}
              </AccountInfo>
            </AccountRow>
          </Card>

          <PlanCreditsCard
            credits={credits}
            onBuyCredits={() => setBuyingCredits(true)}
            onManageBilling={manageBilling}
            billingBusy={billingBusy}
            billingError={billingError}
          />
        </SummaryGrid>

        <CreditActivityCard state={activityState} entries={activity} />
        {buyingCredits && (
          <TopUpSheet
            balance={credits ? credits.balance : null}
            returnPath={ACCOUNT_CREDITS_PATH}
            onClose={() => setBuyingCredits(false)}
          />
        )}

        <PasswordCard passwordSet={passwordSet} onSaved={() => setPasswordSet(true)} />

        <div ref={brandingRef}>
          <BrandingCard
            branding={account.branding || {}}
            onSaved={(branding) =>
              setAccount((currentAccount) =>
                currentAccount ? { ...currentAccount, branding } : currentAccount
              )
            }
          />
        </div>

        <CmaCard
          cmaParams={account.cma_params || {}}
          onSaved={(cmaParams) =>
            setAccount((currentAccount) =>
              currentAccount ? { ...currentAccount, cma_params: cmaParams } : currentAccount
            )
          }
        />

        <SectionsCard
          templates={account.report_templates || []}
          defaultTemplate={account.default_template || ""}
          onSaved={(templates, defaultTemplate) =>
            setAccount((currentAccount) =>
              currentAccount
                ? {
                    ...currentAccount,
                    report_templates: templates,
                    default_template: defaultTemplate,
                  }
                : currentAccount
            )
          }
        />
      </>
    );
  }

  return (
    <PageContainer>
      <NewNavbar />
      <ContentWrapper>
        <Container style={{ maxWidth: 940 }}>
          <HeaderRow>
            <TitleBlock>
              <Eyebrow>Listino Account</Eyebrow>
              <Title>Your account</Title>
              <Subtitle>
                Profile, plan &amp; credits, and prep defaults — find your properties under{" "}
                <Link to="/dashboard">Your listings</Link>.
              </Subtitle>
            </TitleBlock>
            <SignOut type="button" onClick={signOut}>
              Sign out
            </SignOut>
          </HeaderRow>
          {body}
        </Container>
      </ContentWrapper>
      <NewFooter />
    </PageContainer>
  );
};

export default AccountPage;
